import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import utils from "../../../universal/utils";
import adminFetch from "../../../universal/adminFetch";
import useIsMountedRef from "../../../hooks/useIsMountedRef";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  status: {
    marginTop: theme.spacing(1),
  }
}));

export default function QueueSettings({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [queueClosed, setQueueClosed] = useState(false);
  const [updatedAtMs, setUpdatedAtMs] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/matchmaking-queue/adpass/${userId}`);
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to load matchmaking queue setting');
      if (!isMountedRef.current) return;
      setQueueClosed(data.queueClosed === true);
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : null);
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [basePath, isMountedRef, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async (nextClosed) => {
    if (!userId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/matchmaking-queue/adpass/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ queueClosed: nextClosed === true })
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to save matchmaking queue setting');
      if (!isMountedRef.current) return;
      setQueueClosed(data.queueClosed === true);
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : Date.now());

      if (data.queueClosed === true) {
        const ev = data.eviction;
        const msg = ev
          ? `Queue closed. Evicted ${ev.evictedCount || 0} queued players; reset flags for ${ev.resetCount || 0} user(s).`
          : 'Queue closed.';
        setSuccess(msg);
      } else {
        setSuccess('Queue opened.');
      }
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [basePath, isMountedRef, userId]);

  return (
    <Paper className={classes.paper}>
      <Box className={classes.headerRow}>
        <Box>
          <Typography variant="h6">Matchmaking Queue</Typography>
          <Typography variant="body2" color="textSecondary">
            Close the queue to prevent new matchmaking joins (recommended during incidents). Closing the queue evicts all queued players and clears their in-queue profile flags.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpen((v) => !v)}
          startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box className={classes.row}>
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            Status: {queueClosed ? 'CLOSED' : 'OPEN'}
          </Typography>

          <Button
            variant="contained"
            color={queueClosed ? 'primary' : 'secondary'}
            onClick={() => save(!queueClosed)}
            disabled={loading || saving || !userId}
          >
            {saving ? <CircularProgress size={18} /> : (queueClosed ? 'Open queue' : 'Close queue')}
          </Button>

          <Button
            variant="outlined"
            onClick={load}
            disabled={loading || saving || !userId}
          >
            Refresh
          </Button>
        </Box>

        {updatedAtMs ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Last updated: {new Date(updatedAtMs).toISOString()}
          </Typography>
        ) : null}

        {loading ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Loading…
          </Typography>
        ) : null}

        {error ? (
          <Typography className={classes.status} variant="body2" color="error">
            {error}
          </Typography>
        ) : null}

        {success ? (
          <Typography className={classes.status} variant="body2" style={{ color: '#1b5e20' }}>
            {success}
          </Typography>
        ) : null}
      </Collapse>
    </Paper>
  );
}

